import React, { useEffect, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { HubPrimaryAction } from "../shared/HubPrimaryAction";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { DesktopKpiStrip } from "../shared/DesktopKpiStrip";
import { DeskEmpty, DeskPanel, DeskPill, DeskSearch, DeskTable, DeskTabs, DeskToolbar } from "../desktop/DeskTable";
import { RecIcon, RecThumb } from "./MaterialRecordParts";
import { MaterialsViewToggle, MATERIAL_SORT } from "./MaterialsToolbar";
import { MaterialTile } from "./MaterialTile";
import {
  formatCost,
  formatStock,
  hasLoadedField,
  isServiceType,
  materialTypeLabel,
  matchesTypeFilter,
  usesInventory,
} from "./materialRecordModel";

// The master list + side panel only fits side by side from here (deskKit .dp-grid--main-side).
const SIDE_PANEL_QUERY = "(min-width: 1200px)";

function useSidePanelFits() {
  const read = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(SIDE_PANEL_QUERY).matches
      : false;
  const [fits, setFits] = useState(read);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia(SIDE_PANEL_QUERY);
    const onChange = () => setFits(media.matches);
    onChange();
    media.addEventListener?.("change", onChange);
    return () => media.removeEventListener?.("change", onChange);
  }, []);
  return fits;
}

const SORT_LABELS = [
  { key: MATERIAL_SORT.NAME, label: "Name (A–Z)", needsPricing: false },
  { key: MATERIAL_SORT.COST_ASC, label: "Cost: low to high", needsPricing: true },
  { key: MATERIAL_SORT.COST_DESC, label: "Cost: high to low", needsPricing: true },
];

/**
 * Desktop Materials catalog: KPI strip, one toolbar, catalog table or tiles.
 * Same pattern as the Task Catalog: in normal mode a row tap opens a side
 * detail panel (Open details / Edit); in Select mode a row tap only toggles
 * its checkbox. Where the panel cannot sit beside the list (<1200px) a row
 * tap opens the full details screen, as before.
 */
export function MaterialsDesktop({
  subtitle,
  onBack,
  materials,
  filtered,
  typeOptions,
  typeKey,
  onTypeChange,
  searchTerm,
  onSearch,
  sortKey,
  onSortChange,
  canViewPricing,
  canEditCatalog,
  showImport,
  showSelect,
  selecting,
  selectedCount,
  allSelected,
  isSelected,
  onToggleItem,
  onToggleAll,
  onExitSelection,
  onStartSelection,
  onDeleteSelected,
  batchBusy,
  onImport,
  onAdd,
  onOpen,
  onEdit,
  catalogView,
  onCatalogView,
  imageUrlFor,
  hubError,
  children,
}) {
  const materialCount = materials.filter((m) => !isServiceType(m.material_type)).length;
  const serviceCount = materials.length - materialCount;
  const typeTabs = typeOptions.map((option) => ({
    value: option.key,
    label: option.label,
    count: materials.filter((m) => matchesTypeFilter(m, option.key)).length,
  }));
  const sortOptions = SORT_LABELS.filter((o) => !o.needsPricing || canViewPricing);
  const sidePanelFits = useSidePanelFits();
  const usePanel = sidePanelFits && !selecting;
  const [previewId, setPreviewId] = useState(null);
  const preview = usePanel ? filtered.find((m) => m.material_id === previewId) || null : null;

  // Entering Select mode never carries a previewed row along.
  useEffect(() => {
    if (selecting) setPreviewId(null);
  }, [selecting]);

  const openRow = (m) => {
    if (selecting) {
      onToggleItem(m.material_id);
      return;
    }
    if (usePanel) {
      setPreviewId(m.material_id);
      return;
    }
    onOpen(m);
  };

  const columns = [
    ...(selecting
      ? [
          {
            key: "select",
            label: "",
            width: "52px",
            align: "center",
            primary: true,
            render: (m) => (
              <span className={`dt-check${isSelected(m.material_id) ? " is-on" : ""}`} aria-hidden="true">
                {isSelected(m.material_id) ? <RecIcon name="check" /> : null}
              </span>
            ),
          },
        ]
      : []),
    {
      key: "item",
      label: "Item",
      width: canViewPricing ? "38%" : "50%",
      primary: !selecting,
      render: (m) => (
        <span className="dt-item">
          <RecThumb name={m.material_name} type={m.material_type} imageUrl={imageUrlFor(m)} size="list" />
          <span className="dt-item__text">
            <span className="dt-strong">{m.material_name}</span>
            <span className="dt-sub">{m.sku || "No SKU"}</span>
          </span>
        </span>
      ),
    },
    {
      key: "type",
      label: "Type",
      width: "13%",
      render: (m) => (
        <DeskPill tone={isServiceType(m.material_type) ? "blue" : "gold"}>
          {materialTypeLabel(m.material_type)}
        </DeskPill>
      ),
    },
    {
      key: "unit",
      label: "Unit",
      width: "12%",
      render: (m) => <span className="dt-muted">{m.unit_of_measure || "—"}</span>,
    },
    ...(canViewPricing
      ? [
          {
            key: "cost",
            label: "Unit Cost / Rate",
            align: "right",
            width: "16%",
            render: (m) =>
              hasLoadedField(m, "unit_cost") ? <span className="dt-num">{formatCost(m)}</span> : <span className="dt-muted">—</span>,
          },
          {
            key: "stock",
            label: "In Stock",
            align: "right",
            width: "12%",
            render: (m) =>
              usesInventory(m.material_type) && hasLoadedField(m, "current_inventory") ? (
                <span className="dt-num">{formatStock(m)}</span>
              ) : (
                <span className="dt-muted">—</span>
              ),
          },
        ]
      : []),
  ];

  const activeOption = typeOptions.find((o) => o.key === typeKey);

  const listView =
    catalogView === "thumbnail" ? (
        <div className="mat-grid mat-grid--desk">
          {filtered.map((material) => (
            <MaterialTile
              key={material.material_id}
              material={material}
              canViewPricing={canViewPricing}
              imageUrl={imageUrlFor(material)}
              selecting={selecting}
              selected={isSelected(material.material_id)}
              onToggle={() => onToggleItem(material.material_id)}
              onOpen={openRow}
            />
          ))}
        </div>
    ) : (
        <DeskTable
          testId="materials-table"
          caption="Materials catalog"
          columns={columns}
          rows={filtered}
          rowKey={(m) => m.material_id}
          onOpen={openRow}
          openLabel={(m) =>
            selecting ? `Select ${m.material_name}` : `${usePanel ? "Preview" : "Open"} ${m.material_name}`
          }
          rowClassName={(m) =>
            `${isSelected(m.material_id) || (usePanel && m.material_id === previewId) ? "is-selected " : ""}${
              m.active === false ? "is-muted" : ""
            }`
          }
        />
    );

  return (
    <CrewPilotModuleShell
      title="Materials"
      subtitle={subtitle}
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      primary
      compact
      headerActions={
        <div className="cdh-actions">
          <DeskSearch value={searchTerm} onChange={onSearch} placeholder="Search materials…" ariaLabel="Search materials" />
          {canEditCatalog ? (
            <HubPrimaryAction onClick={onAdd} label="Add Material" ariaLabel="Add Material" />
          ) : null}
        </div>
      }
    >
      <DesktopKpiStrip
        ariaLabel="Catalog summary"
        tiles={[
          { key: "items", value: String(materials.length), label: "Catalog Items", tone: "gold" },
          { key: "mat", value: String(materialCount), label: "Materials", tone: "blue" },
          { key: "svc", value: String(serviceCount), label: "Services", tone: "green" },
        ]}
      />

      <DeskToolbar>
        <DeskTabs options={typeTabs} value={typeKey} onChange={onTypeChange} ariaLabel="Filter by type" />
        <span className="dt-toolbar__grow" />
        {selecting ? (
          <>
            <span className="dt-summary" aria-live="polite">{selectedCount} selected</span>
            <button type="button" className="dt-btn" onClick={onToggleAll} disabled={batchBusy}>
              {allSelected ? "Deselect All" : "Select All"}
            </button>
            {canEditCatalog ? (
              <button type="button" className="dt-btn dt-btn--danger" onClick={onDeleteSelected} disabled={batchBusy || selectedCount === 0}>
                {selectedCount > 0 ? `Delete (${selectedCount})` : "Delete"}
              </button>
            ) : null}
            <button type="button" className="dt-btn" onClick={onExitSelection} disabled={batchBusy}>Cancel</button>
          </>
        ) : (
          <>
            <label className="dt-select">
              <span>Sort</span>
              <select value={sortKey} onChange={(event) => onSortChange(event.target.value)} aria-label="Sort materials">
                {sortOptions.map((o) => (
                  <option key={o.key} value={o.key}>{o.label}</option>
                ))}
              </select>
            </label>
            <MaterialsViewToggle view={catalogView} onChange={onCatalogView} />
            {showImport ? <button type="button" className="dt-btn" onClick={onImport}>Import CSV</button> : null}
            {showSelect ? <button type="button" className="dt-btn" onClick={onStartSelection}>Select</button> : null}
          </>
        )}
      </DeskToolbar>

      {hubError ? <div className="dt-error">{hubError}</div> : null}

      {filtered.length === 0 ? (
        <DeskEmpty title="No materials found">
          {typeKey !== "all" && activeOption ? `No ${activeOption.label} records found.` : "Add items to your catalog to see them here."}
        </DeskEmpty>
      ) : usePanel ? (
        <div className="dp-grid dp-grid--main-side">
          <div style={{ minWidth: 0 }}>{listView}</div>
          <aside className="dp-sticky" aria-label="Material preview" data-testid="material-preview">
            {preview ? (
              <DeskPanel
                title={preview.material_name}
                actions={
                  preview.active === false ? <DeskPill tone="muted">Inactive</DeskPill> : <DeskPill tone="green">Active</DeskPill>
                }
              >
                <dl className="dp-kv dp-kv--tight" style={{ marginBottom: "14px" }}>
                  <dt>SKU</dt>
                  <dd>{preview.sku || "No SKU"}</dd>
                  <dt>Type</dt>
                  <dd>{materialTypeLabel(preview.material_type)}</dd>
                  <dt>Unit</dt>
                  <dd>{preview.unit_of_measure || "—"}</dd>
                  {canViewPricing && hasLoadedField(preview, "unit_cost") ? (
                    <>
                      <dt>Unit Cost / Rate</dt>
                      <dd>{formatCost(preview)}</dd>
                    </>
                  ) : null}
                  {canViewPricing && usesInventory(preview.material_type) && hasLoadedField(preview, "current_inventory") ? (
                    <>
                      <dt>In Stock</dt>
                      <dd>{formatStock(preview)}</dd>
                    </>
                  ) : null}
                </dl>
                {preview.description ? <p className="dp-text">{preview.description}</p> : null}
                <p className="dp-hint">
                  Material already recorded on a project keeps the cost captured when it was recorded, and invoices keep
                  their own line amounts, so editing this item does not change them.
                </p>
                <div className="dp-actions">
                  <button type="button" className="dt-btn" onClick={() => onOpen(preview)}>
                    Open details
                  </button>
                  {canEditCatalog && onEdit ? (
                    <button type="button" className="dt-btn" onClick={() => onEdit(preview)}>
                      Edit
                    </button>
                  ) : null}
                </div>
              </DeskPanel>
            ) : (
              <DeskPanel>
                <p className="dp-hint">Tap a material to see its details here.</p>
              </DeskPanel>
            )}
          </aside>
        </div>
      ) : (
        listView
      )}
      {children}
    </CrewPilotModuleShell>
  );
}
